import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Plus, Minus, Navigation2, Maximize2 } from 'lucide-react';

// Valley Mills coordinates (151 Kent Road, Pudsey, Leeds, LS28 9NF)
// Source: ONS / Royal Mail postcode data for LS28 9NF
const VALLEY_MILLS = [53.790845, -1.651544];
const MAP_ZOOM = 13;
const TILE_SUBDOMAINS = ['a', 'b', 'c', 'd'];
const RING_INNER_STYLE = {
  color: '#b39352',
  weight: 1,
  opacity: 0.35,
  fillOpacity: 0.05,
  fillColor: '#b39352',
};
const RING_OUTER_STYLE = {
  color: '#b39352',
  weight: 1,
  opacity: 0.2,
  fillOpacity: 0.02,
  fillColor: '#b39352',
};
const MAP_STYLE = { height: '100%', width: '100%', background: '#eee7d6' };
const POPUP_TITLE_STYLE = {
  fontSize: 11,
  letterSpacing: '0.18em',
  color: '#b39352',
  fontWeight: 600,
  textTransform: 'uppercase',
};
const POPUP_LABEL_STYLE = {
  fontSize: 15,
  color: '#0d281e',
  fontWeight: 600,
  marginTop: 4,
};
const POPUP_WRAP_STYLE = { fontFamily: 'Inter, sans-serif', minWidth: 160 };

const NEARBY = [
  { id: 'leeds', pos: [53.8008, -1.5491], label: 'Leeds City Centre', sub: '15 min drive' },
  { id: 'bradford', pos: [53.7960, -1.7594], label: 'Bradford', sub: '15 min drive' },
  { id: 'pudsey-station', pos: [53.7876, -1.6480], label: 'New Pudsey Station', sub: 'Nearby · Rail' },
  { id: 'm621', pos: [53.7737, -1.6410], label: 'M621 Junction', sub: '10 min drive' },
  { id: 'm62', pos: [53.7280, -1.6980], label: 'M62 Junction 27', sub: '15 min drive' },
];

// Custom Valley Mills marker
const valleyMillsIcon = L.divIcon({
  className: 'valley-mills-marker',
  html: `
    <div style="position:absolute;left:50%;bottom:0;transform:translateX(-50%);white-space:nowrap;">
      <div style="
        background:#0d281e;
        color:#f1ede5;
        padding:8px 16px;
        font-family:'Cormorant Garamond', serif;
        font-size:14px;
        font-weight:600;
        letter-spacing:1.5px;
        box-shadow:0 6px 20px rgba(0,0,0,0.35);
        border:1px solid #b39352;
      ">VALLEY MILLS</div>
      <div style="
        width:0;height:0;
        border-left:8px solid transparent;
        border-right:8px solid transparent;
        border-top:10px solid #0d281e;
        margin:0 auto;
      "></div>
      <div style="
        width:16px;height:16px;
        background:#b39352;
        border:3px solid #0d281e;
        border-radius:50%;
        margin:-3px auto 0;
        box-shadow:0 0 0 4px rgba(179,147,82,0.25);
      "></div>
    </div>
  `,
  iconSize: [200, 66],
  iconAnchor: [100, 66],
});

// Nearby marker
const nearbyIcon = L.divIcon({
  className: 'nearby-marker',
  html: `
    <div style="
      width:14px;height:14px;
      background:#f1ede5;
      border:2px solid #0d281e;
      border-radius:50%;
      box-shadow:0 2px 6px rgba(0,0,0,0.2);
    "></div>
  `,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function MapControls({ onReset }) {
  const map = useMap();
  return (
    <div className="absolute top-4 right-4 flex flex-col gap-2 z-[1000]">
      <div className="flex flex-col bg-[#f1ede5] shadow-lg border border-[#0d281e]/20 overflow-hidden">
        <button
          onClick={() => map.zoomIn()}
          className="w-10 h-10 flex items-center justify-center hover:bg-[#e6dcb8] border-b border-[#0d281e]/10 transition-colors"
          aria-label="Zoom in"
        >
          <Plus size={16} className="text-[#0d281e]" strokeWidth={2} />
        </button>
        <button
          onClick={() => map.zoomOut()}
          className="w-10 h-10 flex items-center justify-center hover:bg-[#e6dcb8] transition-colors"
          aria-label="Zoom out"
        >
          <Minus size={16} className="text-[#0d281e]" strokeWidth={2} />
        </button>
      </div>
      <button
        onClick={onReset}
        className="w-10 h-10 flex items-center justify-center bg-[#f1ede5] shadow-lg border border-[#0d281e]/20 hover:bg-[#e6dcb8] transition-colors"
        aria-label="Recenter"
      >
        <Navigation2 size={15} className="text-[#0d281e]" strokeWidth={2} />
      </button>
      <button
        onClick={() => {
          const el = map.getContainer().parentElement;
          if (el && el.requestFullscreen) el.requestFullscreen();
        }}
        className="w-10 h-10 flex items-center justify-center bg-[#f1ede5] shadow-lg border border-[#0d281e]/20 hover:bg-[#e6dcb8] transition-colors"
        aria-label="Fullscreen"
      >
        <Maximize2 size={14} className="text-[#0d281e]" strokeWidth={2} />
      </button>
    </div>
  );
}

function Recenter({ trigger }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo(VALLEY_MILLS, 13, { duration: 0.9 });
  }, [trigger, map]);
  return null;
}

export default function InteractiveMap() {
  const [resetKey, setResetKey] = useState(0);
  const mapRef = useRef(null);

  return (
    <div className="relative w-full h-[560px] overflow-hidden shadow-2xl border border-[#0d281e]/15 bg-[#eee7d6]">
      <MapContainer
        center={VALLEY_MILLS}
        zoom={MAP_ZOOM}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={MAP_STYLE}
        ref={mapRef}
      >
        {/* Colorful street-map style tiles (Google Maps-like) */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          subdomains={TILE_SUBDOMAINS}
          maxZoom={19}
        />

        {/* Radius rings around Valley Mills */}
        <CircleMarker center={VALLEY_MILLS} radius={60} pathOptions={RING_INNER_STYLE} />
        <CircleMarker center={VALLEY_MILLS} radius={100} pathOptions={RING_OUTER_STYLE} />

        {/* Nearby POIs */}
        {NEARBY.map((n) => (
          <Marker key={n.id} position={n.pos} icon={nearbyIcon}>
            <Popup className="vm-popup">
              <div style={POPUP_WRAP_STYLE}>
                <div style={POPUP_TITLE_STYLE}>{n.sub}</div>
                <div style={POPUP_LABEL_STYLE}>{n.label}</div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Valley Mills main marker */}
        <Marker position={VALLEY_MILLS} icon={valleyMillsIcon} />

        <MapControls onReset={() => setResetKey((k) => k + 1)} />
        <Recenter trigger={resetKey} />
      </MapContainer>

      {/* Top branding chip */}
      <div className="absolute top-4 left-4 bg-[#0d281e] text-[#f1ede5] px-5 py-2.5 shadow-lg z-[1000]">
        <div className="text-[10px] tracking-[0.28em] text-[#b39352] font-medium">LOCATION</div>
        <div className="text-[13px] font-serif-display tracking-wider mt-0.5">Pudsey, West Yorkshire</div>
      </div>

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0d281e] text-[#f1ede5] px-6 py-4 flex items-center justify-between z-[1000]">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 border border-[#b39352] flex items-center justify-center">
            <MapPin size={16} className="text-[#b39352]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] text-[#b39352] font-medium">VALLEY MILLS</div>
            <div className="text-[13px] text-[#f1ede5]/90 mt-0.5">
              151 Kent Road · Pudsey, Leeds · LS28 9NF
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-8 text-[10px] tracking-[0.22em]">
          <div className="text-right">
            <div className="text-[#b39352]">COORDINATES</div>
            <div className="text-[#f1ede5]/85 mt-0.5">53.7908° N · 1.6515° W</div>
          </div>
          <a
            href="https://www.google.com/maps?q=151+Kent+Road,+Pudsey,+Leeds,+LS28+9NF"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#b39352] hover:text-[#f1ede5] border-b border-[#b39352] pb-0.5 transition-colors"
          >
            OPEN IN MAPS
          </a>
        </div>
      </div>
    </div>
  );
}
